import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// Full-screen gate shown when the logged-in user's account is deactivated.
// Reactivation is simple: one click while logged in.
export function DeactivatedGate() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user || user.status !== "deactivated") return null;

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/me/reactivate");
      setUser({ ...user, status: "active" });
      toast.success("Welcome back — your account is active again");
    } catch { toast.error("Could not reactivate. Please try again."); }
    finally { setBusy(false); }
  };

  const signOut = () => { logout(); navigate("/"); };

  return (
    <div data-testid="deactivated-gate" className="fixed inset-0 z-[100] flex items-center justify-center bg-[#FAF9F6]/95 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-xl">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-2xl">👋</div>
        <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-zinc-900">Welcome back</h1>
        <p className="mt-2 text-zinc-500">Your Nearfluence account is currently deactivated. Your profile and activity are hidden but nothing has been lost.</p>
        <Button data-testid="reactivate-btn" onClick={reactivate} disabled={busy} className="mt-6 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><RotateCcw className="mr-2 h-4 w-4" /> Reactivate my account</>}
        </Button>
        <button data-testid="deactivated-signout" onClick={signOut} className="mt-3 text-sm font-medium text-zinc-400 hover:text-zinc-600">Sign out</button>
      </div>
    </div>
  );
}
